// Light/dark theme, remembered per account. The choice is keyed by the Auth0 user id in
// localStorage, so each account on this browser keeps its own preference. With no stored choice
// the app follows the OS `prefers-color-scheme` (and the toggle reflects it live); the first
// explicit toggle pins `data-theme` on <html>, which overrides the OS in app.css.
//
// Note: this is per-account *per browser*. Cross-device sync would need a server-side user
// preference (no such endpoint exists today).

export const theme = $state({ mode: "light" });

const storageKey = (accountId) => `dashboard-theme:${accountId ?? "anon"}`;
const systemPrefersDark = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches;

let currentAccount = null;
let hasStoredChoice = false;

function apply(explicit) {
  // Only stamp data-theme for an explicit choice; otherwise leave app.css's media query in charge.
  if (explicit) document.documentElement.dataset.theme = theme.mode;
  else delete document.documentElement.dataset.theme;
}

// Called once the account is known (from the app shell). Loads that account's stored choice, or
// falls back to the OS preference.
export function initTheme(accountId) {
  currentAccount = accountId;
  const stored = localStorage.getItem(storageKey(accountId));
  hasStoredChoice = stored === "dark" || stored === "light";
  theme.mode = hasStoredChoice ? stored : systemPrefersDark() ? "dark" : "light";
  apply(hasStoredChoice);
}

// Toggle handler — pins and persists the choice for the current account.
export function setTheme(mode) {
  theme.mode = mode;
  hasStoredChoice = true;
  localStorage.setItem(storageKey(currentAccount), mode);
  apply(true);
}

// The public sign-up page renders light whatever the OS prefers — it has no shell and no theme
// toggle, so a visitor on a dark OS would otherwise get a dark form with no way back. Pins
// data-theme="light" (which every dark rule excludes) and returns the undo, for onMount cleanup.
export function forceLightTheme() {
  const previous = document.documentElement.dataset.theme;
  document.documentElement.dataset.theme = "light";
  return () => {
    if (previous === undefined) delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = previous;
  };
}

// Keep the toggle in sync with the OS while the user hasn't made an explicit choice.
if (typeof window !== "undefined" && window.matchMedia) {
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener?.("change", (e) => {
    if (!hasStoredChoice) theme.mode = e.matches ? "dark" : "light";
  });
}
